import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import { CustomInput, CustomTextarea, CustomToggle } from '../Components/CustomFormElements';
import PageLoader from '../Components/PageLoader';
import { InlineSkillApiProvider, useInlineSkillApi } from '../Services/InlineSkillApiProvider';
import { InlineSkillCreateApi } from '../Services/InlineSkillApiProviderTypes';
import { isEmpty } from '../utils/validationUtil';

function InlineSkillContent({ canEdit }: { canEdit: boolean }) {
  const { organizationId, inlineSkillId } = useParams<{
    organizationId: string;
    inlineSkillId: string;
  }>();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { getInlineSkillByKey, createInlineSkill, updateInlineSkill } = useInlineSkillApi();

  const isNew = inlineSkillId === 'new-inline-skill';
  const isDisabled = !canEdit;

  const [loading, setLoading] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [definition, setDefinition] = useState<string>('');
  const [isPreloaded, setIsPreloaded] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(true);

  const inlineSkillsUrl = `/organizations/${organizationId}/prompts-hub?tab=inline-skills`;

  useEffect(() => {
    const loadInlineSkill = async () => {
      if (isNew) return;
      setLoading(true);
      try {
        const response = await getInlineSkillByKey(inlineSkillId!);
        setName(response.name);
        setDescription(response.description);
        setDefinition(response.definition);
        setIsPreloaded(response.is_preloaded);
        setIsActive(response.is_active);
      } catch (error) {
        showAlert('fail', 'Failed to fetch inline skill');
      } finally {
        setLoading(false);
      }
    };
    loadInlineSkill();
  }, [inlineSkillId, isNew, getInlineSkillByKey, showAlert]);

  const onSave = async () => {
    if (isEmpty(name)) {
      showAlert('fail', 'Please enter inline skill name');
      return;
    }

    const data: InlineSkillCreateApi = {
      name,
      description,
      definition,
      is_preloaded: isPreloaded,
      is_active: isActive,
    };

    setLoading(true);

    try {
      if (isNew) {
        await createInlineSkill(data);
        showAlert('success', 'Inline skill created successfully!');
      } else {
        await updateInlineSkill(inlineSkillId!, data);
        showAlert('success', 'Inline skill updated successfully!');
      }
      navigate(inlineSkillsUrl);
    } catch (error) {
      console.error('Error saving inline skill:', error);
      showAlert('fail', 'Failed to save the inline skill.');
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    navigate(inlineSkillsUrl);
  };

  if (loading) return <PageLoader />;

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-6">
          <h2 className="h2-title">{name || 'New Inline Skill'}</h2>
          <div className="gap d-flex flex-row-to-col flex-column">
            <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
              <h4 className="h4-title">Main Settings</h4>

              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Name</div>
                  <div className="small-font">Inline skill name</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomInput
                    id="inline-skill-name"
                    label="Inline Skill Name"
                    placeholder="Inline skill name"
                    icon={<i className="fa-solid fa-right-to-bracket"></i>}
                    type="text"
                    value={name}
                    onChange={(_id: string, value: string) => setName(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Preload</div>
                  <div className="small-font">
                    Add the definition to the agent context without loading it on demand
                  </div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomToggle
                    id="inline-skill-preload"
                    label={isPreloaded ? 'Preloaded' : 'On demand'}
                    checked={isPreloaded}
                    onChange={(_id: string, value: boolean) => setIsPreloaded(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Activate Inline Skill</div>
                  <div className="small-font">
                    Set inline skill to be active or inactive to enable or disable it
                  </div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomToggle
                    id="inline-skill-status"
                    label={isActive ? 'Active' : 'Inactive'}
                    checked={isActive}
                    onChange={(_id: string, value: boolean) => setIsActive(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className="mb-4 mt-5">
                <CustomTextarea
                  id="inline-skill-description"
                  label="Description"
                  disabled={isDisabled}
                  addedText={description}
                  onChange={(value: string) => setDescription(value)}
                  resizable={false}
                  height="120px"
                />
              </div>

              <div className="mb-4">
                <CustomTextarea
                  id="inline-skill-definition"
                  label="Definition"
                  disabled={isDisabled}
                  addedText={definition}
                  onChange={(value: string) => setDefinition(value)}
                  resizable={false}
                  height="300px"
                />
              </div>
            </div>

            <div className="buttons-container d-flex justify-end">
              <Button variant="primary" size="medium" disabled={isDisabled} onClick={onSave}>
                Save
              </Button>
              <Button variant="danger" size="medium" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InlineSkill({ canEdit }: { canEdit: boolean }) {
  return (
    <ConfirmationProvider>
      <InlineSkillApiProvider>
        <InlineSkillContent canEdit={canEdit} />
      </InlineSkillApiProvider>
    </ConfirmationProvider>
  );
}

export default InlineSkill;
